(function () {
  /**
   * ============================================================
   *  Evitavonni — B2B Trade Account form: styling
   * ============================================================
   *  This script visually customizes the Shopify Forms embed (B2B
   *  trade account form) — brand styling, section headings, checkbox
   *  layout, mobile behavior. It deliberately does NOT touch any other
   *  Shopify Forms form that might exist elsewhere on the site (see
   *  isTargetForm below).
   *
   *  Table of contents (search for the number, e.g. "3." to find a block):
   *   1. FONT_CSS           — brand font, colors, buttons
   *   2. SECTIONS / SECTION_CSS — section headings (Company Details, etc.)
   *   3. LAYOUT_CSS         — layout, underline fields, checkbox boxes
   *   4. MOBILE_CSS         — responsive behavior + iOS zoom fix
   *   5. IMAGE_CSS + insertClosingImage() — image above submit button
   *   6. applySections() / forceCheckboxLayout() — DOM adjustments
   *   7. init() / bootstrap  — how and when all of this runs
   *
   *  Fault tolerance: every step in init() runs via safeRun(), so if
   *  one part unexpectedly fails (e.g. after a Shopify update changes
   *  a class name), the other parts keep running instead of the whole
   *  form staying unstyled. Check the browser console for
   *  "[ev-form-sections]" messages if something looks off.
   *
   *  Note: pagination (Back/Next steps) was removed on purpose after
   *  it caused issues with page layout/heights — the form is now a
   *  single continuous page again, styled top to bottom.
   * ============================================================
   */

  // Production safeguard: prevents this script from running twice if
  // it (accidentally) gets loaded twice on the same page.
  if (window.__evFormSectionsLoaded) return;
  window.__evFormSectionsLoaded = true;

  // Production safeguard: this script relies on the CSS :has() selector
  // for the checkbox/radio layout. Widely supported since 2023 (Chrome
  // 105+, Safari 15.4+, Firefox 121+), but we log it clearly if a
  // visitor's browser doesn't support :has(), so this doesn't silently
  // turn into an unexplained "why does this look wrong".
  try {
    if (!CSS.supports('selector(:has(*))')) {
      console.warn(
        '[ev-form-sections] This browser does not support the CSS :has() selector — ' +
        'some styling (checkbox/radio boxes) will fall back to the embed\'s default look.'
      );
    }
  } catch (err) {
    // CSS.supports with a selector string is itself fairly new;
    // if this fails, just ignore it silently.
  }

  /**
   * 1. Font: Futura PT everywhere (body, labels, headings, buttons) —
   *    carried over from the old Globo form CSS, kept simple with one
   *    typeface rather than a separate heading font.
   *
   *    <shopify-forms-embed> sets its typography via CSS custom
   *    properties on :host (--forms-body-font, --forms-heading-font,
   *    etc. — visible in the <style> the embed itself already places
   *    in the shadow root). By overriding the same variables in OUR
   *    <style> (which we add AFTER the embed's own stylesheet), all
   *    internal components that use these variables automatically pick
   *    up the new font — more robust than chasing individual classes.
   *
   *    For things that aren't available as a variable (uppercase,
   *    letter-spacing, button padding) we use stable selectors
   *    (tag names / data-testid), just like in the old CSS, instead of
   *    the hashed classes.
   */
  const FONT_CSS = `
    :host {
      --forms-body-font: normal 400 14px "futura-pt", sans-serif !important;
      --forms-heading-font: normal 700 24px "futura-pt", sans-serif !important;
      --button-text-color: #ffffff !important;
      --button-border-radius: 0px !important;

      /* Evitavonni brand colors (from Project: Brand assets Evitavonni) */
      --ev-vellum-white: #F1EFEB;
      --ev-ink-navy: #120E27;
      --ev-soft-mist: #D7D2CB;
      --ev-dark-mist: #8B867E;
      --ev-taupe: #B7ACA2;
      --ev-charcoal: #2B2C2D;
      --ev-stone-grey: #B4B4B4;
    }

    label,
    legend {
      font-family: "futura-pt", sans-serif !important;
      letter-spacing: 0.2em !important;
      text-transform: uppercase !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      line-height: 1.5em !important;
      text-decoration: none !important;
      color: var(--ev-dark-mist) !important;
    }

    /* Date field (MM/DD/YYYY): several attempts to fix the overlap
       issue (label stays on top of the entered value) didn't give a
       lasting result — deliberately left untouched for now, to be
       revisited later. */
    /* NOTE: don't use button[type="button"] — the country dropdowns
       (e.g. on Company billing address) are also <button type="button">
       and accidentally got white text (invisible against the light
       background) from that. So here we only target the submit button
       and the Signature "Add file" button, via their stable attributes. */
    [data-testid="btn-form-submit"],
    [id="custom#signature-label"] {
      font-family: "futura-pt", sans-serif !important;
      color: #ffffff !important;
      background-color: var(--ev-ink-navy) !important;
      text-transform: uppercase !important;
      font-weight: 600 !important;
      border-radius: 0 !important;
      text-align: center !important;
      letter-spacing: 0.2em !important;
      padding: 16px 32px !important;
      font-size: 11px !important;
      line-height: 1em !important;
    }
  `;

  /**
   * 2. Section headings for the Shopify Forms B2B trade-account form.
   *    The form lives inside <shopify-forms-embed> with declarative
   *    shadow DOM. We anchor on stable attributes (data-testid /
   *    fieldset[name]), not on the hashed CSS classes from devtools.
   */
  // type "insert": adds a new heading div before the anchor (for
  //   fields without their own visible label, or where the text differs).
  // type "legend": these fieldsets already show their label as a
  //   <legend> with the exact same text as the desired section title —
  //   so instead of adding a new div (which caused duplicate text), we
  //   give the existing legend the heading style directly.
  const SECTIONS = [
    {
      type: 'insert',
      title: 'Company Details',
      find: (form) => form.querySelector('[data-testid="field-company_name"]')?.parentElement,
    },

    {
      type: 'legend',
      find: (form) => form.querySelector('fieldset[name="custom#type_of_business"]'),
    },

    {
      type: 'legend',
      find: (form) => form.querySelector('fieldset[name="custom#number_of_employees"]'),
    },

    {
      type: 'legend',
      find: (form) => form.querySelector('fieldset[name="custom#display"]'),
    },

    {
      type: 'legend',
      find: (form) => form.querySelector('fieldset[name="custom#typical_project_size"]'),
    },

    {
      type: 'legend',
      find: (form) => form.querySelector('fieldset[name="custom#estimated_spend_evitavonni"]'),
    },

    {
      type: 'legend',
      find: (form) => form.querySelector('fieldset[name="custom#products_of_interest"]'),
    },

    {
      type: 'insert',
      title: 'Trade Reference 1',
      find: (form) => form.querySelector('[data-testid="field-custom#company_name"]')?.parentElement,
    },

    {
      type: 'insert',
      title: 'Trade Reference 2',
      find: (form) => form.querySelector('[data-testid="field-custom#company_name_1"]')?.parentElement,
    },

    {
      type: 'insert',
      title: 'Conditions of Trading',
      find: (form) => form.querySelector('fieldset[name="custom#evitavonnis_conditions_of_trading"]'),
    },
  ];

  const SECTION_CSS = `
    .ev-form-section-title {
      font-family: "futura-pt", sans-serif !important;
      font-size: 16px !important;
      font-weight: 600 !important;
      letter-spacing: 0.05em !important;
      text-transform: uppercase !important;
      color: var(--ev-charcoal);
      margin: 80px 0 40px;
      grid-column: 1 / -1;
    }

    .ev-form-section-title--first {
      margin-top: 0;
    }

    /* Safety net: any legend that hasn't already received the
       .ev-form-section-title class via SECTIONS above (e.g. "Company
       Billing Address", whose exact fieldset name we don't know) still
       gets the same heading look and spacing, so no "orphan" heading is
       left behind on the small label style. A standalone
       .ev-form-section-title (class selector) always wins over this,
       so this doesn't override anything that was already correct. */
    legend[class] {
      font-size: 16px !important;
      font-weight: 600 !important;
      letter-spacing: 0.05em !important;
      color: var(--ev-charcoal) !important;
      margin: 80px 0 40px !important;
    }
  `;

  /**
   * 3. Layout: wider, single-column layout for standalone text fields.
   *    Fieldsets, address lines, date, signature, and the submit
   *    button stay full-width; only the simple text fields (and the
   *    phone field) are allowed to sit side by side.
   */
  const LAYOUT_CSS = `
    /* The embed constrains itself internally via these variables (seen
       in the embed's own <style>: --container-max-w: 397px,
       --inline-container-max-width: 450px). We override those too,
       otherwise the wider wrapper above has no effect. */
    :host {
      --container-max-w: 700px !important;
      --inline-container-max-width: 700px !important;
    }

    /* Single column, with generous space between fields — like the
       withdrawal form: every field gets breathing room above and below. */
    form[data-testid="form"] {
      display: flex !important;
      flex-direction: column !important;
      gap: 32px !important;
    }

    /* The dialog wrapper around the form gets an inline min-height from
       the embed itself (via --inline-container-min-height), calculated
       based on the longest page at load time. When paginating to
       shorter steps, that height stayed stuck — hence the large empty
       space. We force it back to auto. */
    section[data-sizing="form-wrapper"] {
      min-height: auto !important;
    }

    /* Checkbox and radio options (e.g. "Type of Business") each get
       their own bordered box, with spacing between them — like the
       reference example. We anchor on the input type itself rather
       than hashed classes, which stays stable regardless of how
       Shopify names the wrapper divs. */
    fieldset label:has(input[type="checkbox"]),
    fieldset label:has(input[type="radio"]) {
      display: flex !important;
      align-items: center !important;
      gap: 16px !important;
      border: 1px solid var(--ev-stone-grey) !important;
      padding: 20px 24px !important;
      margin: 0 0 12px !important;
      text-transform: uppercase;
    }

    fieldset > div,
    fieldset > [role="group"],
    fieldset div:has(> label:has(input[type="checkbox"])),
    fieldset div:has(> label:has(input[type="radio"])),
    fieldset:has(> label:has(input[type="checkbox"])),
    fieldset:has(> label:has(input[type="radio"])),
    fieldset [class*="horizontalLayout" i] {
      display: flex !important;
      flex-direction: column !important;
      grid-template-columns: none !important;
      width: 100% !important;
    }

    fieldset label:has(input[type="checkbox"]),
    fieldset label:has(input[type="radio"]) {
      width: 100% !important;
    }

    /* The embed renders standalone text fields by default as fully
       bordered boxes (label + value in one box). We replace that with
       the quieter underline style: no border around it, just a line
       under the field. We anchor on the "formFieldContainer" /
       "formPhoneInputContainer" classes (partial match via [class*=…]
       since Shopify adds a hash) and on the input/select/textarea itself. */
    form[data-testid="form"] [class*="formFieldContainer" i],
    form[data-testid="form"] [class*="formPhoneInputContainer" i] {
      border: none !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    form[data-testid="form"] input:not([type="checkbox"]):not([type="radio"]),
    form[data-testid="form"] textarea,
    form[data-testid="form"] select,
    form[data-testid="form"] [role="combobox"],
    form[data-testid="form"] button[aria-haspopup="listbox"],
    form[data-testid="form"] [class*="select" i] button {
      border: none !important;
      border-bottom: 1px solid var(--ev-stone-grey) !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    form[data-testid="form"] input:not([type="checkbox"]):not([type="radio"]):focus,
    form[data-testid="form"] textarea:focus,
    form[data-testid="form"] select:focus,
    form[data-testid="form"] [role="combobox"]:focus,
    form[data-testid="form"] button[aria-haspopup="listbox"]:focus {
      outline: none !important;
      border-bottom-color: var(--ev-ink-navy) !important;
    }

    /* The floating label (e.g. "Trade Reference 1 - Company Name") was
       overlapping the entered value. The embed determines the position
       via "transform: var(--input-label-transform)" plus a separate,
       hardcoded inline "top" value — overriding the variable had too
       little effect next to that fixed top value. So we override the
       transform property itself directly, with a sizeable value, so it
       doesn't depend on the var() chain. */
    form[data-testid="form"] [class*="formFieldContainer" i] [class*="inputFilled" i] {
      transform: translateY(-22px) scale(0.85) !important;
    }

    /* Country selector and phone number each with their own underline
       (separate fields), as it was before our last change. */
    form[data-testid="form"] [class*="formPhoneInputContainer" i] {
      display: flex !important;
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 16px !important;
      width: 100% !important;
      padding-top: 8px !important;
    }

    form[data-testid="form"] [class*="formPhoneInputContainer" i] > [class*="formFieldContainer" i] {
      flex: 1 1 auto !important;
      width: auto !important;
    }

    form[data-testid="form"] [class*="formPhoneInputContainer" i] input {
      width: 100% !important;
    }

    /* We deliberately leave the flag/country selector completely
       untouched — every attempt to adjust it (border, padding,
       overflow, height, object-fit) either cropped the flag, made it
       disappear, or broke the dropdown. We no longer touch this
       element. */
  `;

  /**
   * 4. Mobile: the embed is already partly responsive (e.g. paired
   *    fields like First/Last name and City/Postal code already stack
   *    themselves), but our own additions (generous desktop spacing,
   *    section titles, checkbox boxes) aren't automatically. We fix
   *    that here, plus an important iOS bug: Safari automatically
   *    zooms in on an <input> if its font-size is < 16px —
   *    --forms-body-font is 14px, so we force it to 16px on the fields
   *    themselves (fine on desktop too).
   */
  const MOBILE_CSS = `
    form[data-testid="form"] input,
    form[data-testid="form"] textarea,
    form[data-testid="form"] select {
      font-size: 16px !important;
    }

    :host {
      box-sizing: border-box;
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: inherit;
    }

    @media (max-width: 640px) {
      :host {
        --container-max-w: 100% !important;
        --inline-container-max-width: 100% !important;
      }

      form[data-testid="form"] {
        gap: 28px !important;
        padding: 0 20px !important;
      }

      .ev-form-section-title,
      legend[class] {
        margin: 40px 0 24px !important;
        font-size: 14px !important;
      }

      .ev-form-section-title--first {
        margin-top: 0 !important;
      }

      label,
      legend {
        font-size: 10px !important;
        letter-spacing: 0.12em !important;
      }

      fieldset label:has(input[type="checkbox"]),
      fieldset label:has(input[type="radio"]) {
        padding: 16px !important;
        gap: 12px !important;
      }

      /* Phone field: country button and number stacked instead of
         side by side, so there's enough room to type on narrow screens. */
      form[data-testid="form"] [class*="formPhoneInputContainer" i] {
        flex-direction: column !important;
        align-items: stretch !important;
      }

      [data-testid="btn-form-submit"] {
        width: 100% !important;
        padding: 16px 24px !important;
      }
    }
  `;

  /**
   * 5. Image above the last section ("Conditions of Trading").
   */
  const IMAGE_CSS = `
    .ev-form-closing-image {
      display: block;
      width: 100%;
      height: auto;
      margin: 80px 0 0;
      grid-column: 1 / -1;
    }
  `;

  const CLOSING_IMAGE_URL =
    'https://cdn.shopify.com/s/files/1/0830/2782/0882/files/Screenshot_2025-08-15_at_11.33.14.webp?v=1784815575';

  function insertClosingImage(form) {
    if (!form || form.dataset.evImageInserted === 'true') return;

    const submitBtn = form.querySelector('[data-testid="btn-form-submit"]');
    // The submit button is usually inside its own wrapper div that is
    // a top-level child of the form; we insert the image right before
    // that, so it appears on the same (last) page as the button.
    const anchor = submitBtn ? (submitBtn.closest('form > *') || submitBtn) : null;
    if (!anchor || !anchor.parentNode) return;

    const img = document.createElement('img');
    img.src = CLOSING_IMAGE_URL;
    img.alt = '';
    img.className = 'ev-form-closing-image';
    anchor.parentNode.insertBefore(img, anchor);

    form.dataset.evImageInserted = 'true';
  }

  function injectStyles(shadow) {
    if (shadow.querySelector('style[data-ev-styles]')) return;

    const style = document.createElement('style');
    style.setAttribute('data-ev-styles', 'true');
    style.textContent = FONT_CSS + SECTION_CSS + LAYOUT_CSS + MOBILE_CSS + IMAGE_CSS;
    shadow.appendChild(style);
  }

  function applySections(form) {
    if (!form || form.dataset.evSectionsApplied === 'true') return;

    let isFirst = true;
    let applied = 0;

    SECTIONS.forEach((section) => {
      try {
        let anchor;

        try {
          anchor = section.find(form);
        } catch (e) {
          anchor = null;
        }

        if (!anchor) return;

        if (section.type === 'legend') {
          const legend = anchor.querySelector('legend');
          if (!legend || legend.classList.contains('ev-form-section-title')) return;

          legend.classList.add('ev-form-section-title');
          if (isFirst) legend.classList.add('ev-form-section-title--first');
          isFirst = false;
          applied++;
          return;
        }

        // type === 'insert'
        if (!anchor.parentNode) return;

        const heading = document.createElement('div');
        heading.className = 'ev-form-section-title' + (isFirst ? ' ev-form-section-title--first' : '');
        heading.textContent = section.title;
        anchor.parentNode.insertBefore(heading, anchor);
        isFirst = false;
        applied++;
      } catch (err) {
        // One broken section shouldn't block the other sections.
        console.error('[ev-form-sections] applySections (section "' + (section.title || section.type) + '") failed:', err);
      }
    });

    if (applied > 0) {
      form.dataset.evSectionsApplied = 'true';
    }

    return applied;
  }

  function forceCheckboxLayout(form) {
    if (!form) return;

    // Safety net, separate from the CSS above: also apply the layout
    // directly via inline style on the elements themselves. An inline
    // style set via JS (element.style...) always wins over rules in an
    // external stylesheet (even !important ones, unless the inline
    // style itself later gets overridden by something else) — so this
    // works regardless, as soon as this script is actually active on
    // the page.
    form.querySelectorAll('fieldset').forEach((fieldset) => {
      try {
        const hasChoices = fieldset.querySelector('input[type="checkbox"], input[type="radio"]');
        if (!hasChoices) return;

        // The wrapper around the options (e.g. "_horizontalLayout_...")
        // — anchored on the stable part of the class name.
        fieldset.querySelectorAll('[class*="horizontalLayout" i], [class*="checkboxLayout" i], [class*="radioLayout" i]').forEach((wrapper) => {
          wrapper.style.setProperty('display', 'flex', 'important');
          wrapper.style.setProperty('flex-direction', 'column', 'important');
          wrapper.style.setProperty('grid-template-columns', 'none', 'important');
          wrapper.style.setProperty('width', '100%', 'important');
        });

        // Also the direct div children of the fieldset itself
        // (fallback, in case the wrapper has none of the class names above).
        Array.from(fieldset.children).forEach((child) => {
          if (child.tagName === 'DIV') {
            child.style.setProperty('display', 'flex', 'important');
            child.style.setProperty('flex-direction', 'column', 'important');
            child.style.setProperty('grid-template-columns', 'none', 'important');
            child.style.setProperty('width', '100%', 'important');
          }
        });

        fieldset.querySelectorAll('label:has(input[type="checkbox"]), label:has(input[type="radio"])').forEach((label) => {
          label.style.setProperty('width', '100%', 'important');
        });
      } catch (err) {
        // One unexpectedly broken fieldset shouldn't block the rest.
        console.error('[ev-form-sections] forceCheckboxLayout (fieldset) failed:', err);
      }
    });
  }

  // Production safeguard: this script may be loaded site-wide (e.g. via
  // theme.liquid), while <shopify-forms-embed> could also be used by
  // other, completely unrelated forms elsewhere on the site (e.g. a
  // newsletter popup). We always check whether the form we found is
  // REALLY this B2B trade-account form, via a field that only exists
  // in it — otherwise we might accidentally touch a completely
  // different form.
  function isTargetForm(form) {
    return !!(form && form.querySelector('[data-testid="field-company_name"]'));
  }

  function getShadowAndForm() {
    const hosts = document.querySelectorAll('shopify-forms-embed');

    for (const host of hosts) {
      const shadow = host.shadowRoot;
      const form = shadow && shadow.querySelector('form[data-testid="form"]');

      if (form && isTargetForm(form)) {
        return { shadow, form };
      }
    }

    return { shadow: null, form: null };
  }

  function safeRun(label, fn) {
    try {
      fn();
    } catch (err) {
      console.error('[ev-form-sections] ' + label + ' failed:', err);
    }
  }

  function init() {
    const { shadow, form } = getShadowAndForm();

    if (shadow && form) {
      safeRun('injectStyles', () => injectStyles(shadow));
      safeRun('insertClosingImage', () => insertClosingImage(form));
      safeRun('applySections', () => applySections(form));
      safeRun('forceCheckboxLayout', () => forceCheckboxLayout(form));
      return true;
    }

    return false;
  }

  try {
    // 1. Try immediately: with declarative shadow DOM the content is
    //    often already there.
    if (init()) return;

    // 2. <shopify-forms-embed> hosts can be added to the page late, and
    //    their shadow root often fills in asynchronously (React
    //    hydration) — mutations INSIDE a shadow root don't bubble up to
    //    document.documentElement, so we need two layers:
    //    (a) an observer on the page that detects new hosts, and
    //    (b) a dedicated observer per host, directly on its shadow
    //    root, that catches the actual hydration.
    //
    //    NOTE: this script sits without defer in <head>, so <body>
    //    doesn't exist yet here. We therefore observe document.documentElement.
    const MAX_WAIT_MS = 20000;
    const startedAt = Date.now();
    const observedHosts = new WeakSet();
    let settled = false;

    function tryInit() {
      if (settled) return true;

      try {
        if (init()) {
          settled = true;
          return true;
        }
      } catch (err) {
        // One failed attempt shouldn't block the rest of the page; we
        // just try again on the next mutation.
        console.error('[ev-form-sections] init attempt failed:', err);
      }

      return false;
    }

    function watchHostsForShadowUpdates() {
      document.querySelectorAll('shopify-forms-embed').forEach((host) => {
        if (observedHosts.has(host)) return;

        const attach = () => {
          if (settled || observedHosts.has(host)) return;

          const shadow = host.shadowRoot;
          if (!shadow) {
            // Shadow root not available yet (or ever "closed" instead
            // of "open") — don't keep polling, just wait for the next
            // round from the outer observer/timeout.
            return;
          }

          observedHosts.add(host);

          const shadowObserver = new MutationObserver(() => tryInit());
          shadowObserver.observe(shadow, { childList: true, subtree: true });
          setTimeout(() => shadowObserver.disconnect(), MAX_WAIT_MS);
        };

        attach();
      });
    }

    const rootObserver = new MutationObserver(() => {
      if (tryInit()) {
        rootObserver.disconnect();
        return;
      }

      watchHostsForShadowUpdates();

      if (Date.now() - startedAt > MAX_WAIT_MS) {
        rootObserver.disconnect();
      }
    });

    rootObserver.observe(document.documentElement, { childList: true, subtree: true });
    watchHostsForShadowUpdates(); // also try immediately, in case hosts already exist
    setTimeout(() => rootObserver.disconnect(), MAX_WAIT_MS);
  } catch (err) {
    console.error('[ev-form-sections] init failed:', err);
  }
})();